/**
 * Presentation metadata for `OptionTier`.
 *
 * Purpose
 *   Option tiers must NEVER be conveyed by colour alone (WCAG 1.4.1). Every tier therefore
 *   carries redundant encodings: a Lucide icon, a text label, and a brand colour token (the
 *   badge no longer prints the numeric rank). This module is the single place that mapping
 *   lives, shared by the web UI and any tooling. See docs/brand.md and docs/game-design.md.
 *
 * Colour tokens (`--tier-*`) are defined in apps/web's Tailwind theme, derived from the
 * Borenius semantic palette: best ≈ --success (green), good ≈ amber, weak ≈ --warning
 * (orange), worst ≈ --danger (red).
 */
import type { OptionTier } from "./schema";

export interface TierMeta {
  /** 1 (best) … 4 (worst) — ordering metadata (extraction/sorting; not displayed). */
  rank: 1 | 2 | 3 | 4;
  /** The source colour name, for traceability to the spec's colour coding. */
  sourceColor: "green" | "yellow" | "orange" | "red";
  /** Human-readable label shown alongside the option. */
  label: string;
  /** Lucide icon name (no emoji — brand rule). */
  icon: string;
  /** CSS custom property the web app maps to a brand colour. */
  colorVar: `--tier-${string}`;
}

export const TIER_META: Record<OptionTier, TierMeta> = {
  best: { rank: 1, sourceColor: "green", label: "Best", icon: "check-check", colorVar: "--tier-best" },
  good: { rank: 2, sourceColor: "yellow", label: "Reasonable", icon: "check", colorVar: "--tier-good" },
  weak: { rank: 3, sourceColor: "orange", label: "Weak", icon: "alert-triangle", colorVar: "--tier-weak" },
  worst: { rank: 4, sourceColor: "red", label: "Avoid", icon: "x", colorVar: "--tier-worst" },
};

/** Reverse lookup: source rank (1–4) → tier key. */
export const TIER_BY_RANK: Readonly<Record<1 | 2 | 3 | 4, OptionTier>> = {
  1: "best",
  2: "good",
  3: "weak",
  4: "worst",
};

export function tierMeta(tier: OptionTier): TierMeta {
  return TIER_META[tier];
}
